import React from "react";
import {
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Chip,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { FilteredData } from "./SearchWorkflowContainer";
import MainNameLinefeed from "./components/MainNameInLineFeed";
import theme from "../../style/theme";
import ClearSharpIcon from '@mui/icons-material/ClearSharp';

// 内部処理から画面側に渡す値や関数を宣言するもの
export type SearchWorkflowProps = {
  filteredData: FilteredData[];
  allLoaded: boolean;
  loading: boolean;
  handleCardClick: (id: string) => void;
  sortOrder: "asc" | "desc";
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  toggleSortOrder: () => void;
  handleSearchTextClear: () => void;
};

/**
 * 申請書一覧画面コンポーネント（表示層）
 * - 検索・ソート・カード形式での申請一覧表示を担当する
 * - props: filteredData (フィルタ済みデータ), loading (読み込み状態),
 *          handleCardClick (カードクリックコールバック), searchText (検索テキスト),
 *          setSearchText (検索テキスト更新), toggleSortOrder (ソート切り替え)
 */
export default function SearchWorkflow(props: SearchWorkflowProps) {
  // 内部処理側から渡された変数名を同じように利用するための宣言
  const { filteredData, allLoaded, loading, handleCardClick, sortOrder, searchText, setSearchText, toggleSortOrder, handleSearchTextClear } =
    props;

  //Loading表示
  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <Box sx={{ m: 2, mx: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginTop: 1,
          }}
        >
          <TextField
            label="フィルタを入力"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => {
              const value = e.target.value;
              setSearchText(value);
              // 空の場合はsessionStorageを削除。それ以外は値を格納する。
              if (value === "") {
                sessionStorage.removeItem("searchText_SearchWorkflow");
              } else {
                sessionStorage.setItem("searchText_SearchWorkflow", value);
              }
            }}
            sx={{ width: 220 }}
            slotProps={{
              input: {
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton
                    onClick={handleSearchTextClear} 
                    edge="end" 
                    sx={{
                      backgroundColor: 'transparent', // 背景透明
                      '&:hover': {
                        backgroundColor: 'transparent', // ホバー時も透明
                      }
                    }}>
                      <ClearSharpIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }}
          />
          {/* 順序並びボタン */}
        <Button onClick={toggleSortOrder}>
          {sortOrder === "asc" ? "申請書名 降順" : "申請書名 昇順"}
        </Button>
        </Box>
        {filteredData.map((item) => (
          <Card
            key={item.id}
            sx={{
              my: 3,
              cursor: "default", // カーソルを矢印に固定
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                // transform: 'scale(1.02)',
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                backgroundColor: "#f9f9f9",
              },
            }}
            onClick={(event) => {
              event.stopPropagation();
              handleCardClick(item.id);
            }}
          >
            <CardContent>
              <MainNameLinefeed item={item} />
            </CardContent>
          </Card>
        ))}
        {allLoaded && (
          <Typography
            variant="body1"
            sx={{ marginTop: 2, textAlign: "center" }}
          >
            これ以上ありません！
          </Typography>
        )}
      </Box>
    </>
  );
}
